import type { DrillType } from "@-/dev.improve.haniverse.lib.drill.entities";

/**
 * Which kind of content a `user_card` is about.
 *
 * `user_card` itself is content-agnostic — it carries only scheduling state (`fsrsCardId`,
 * `startedStudyingAt`, `skippedAt`, `manuallyMasteredAt`). A card's identity lives in an
 * *overlay* table keyed by a unique `userCardId`. This union is the discriminator that
 * overlay set never had: before it, "which kind is this card" was inferred from which of two
 * named relations happened to be non-null, an idiom that is only correct while there are
 * exactly two kinds.
 *
 * A `quiz` card is the one kind that is not a drill: it is graded by the learner's answer to an
 * authored `:::quiz` (see `assets/slides`), so it has no `DrillType`. {@link DrillCardContentKind}
 * names the others.
 */
export type CardContentKind = DrillCardContentKind | "quiz";

/** Every card kind that is drilled, i.e. has a `DrillType` and a rated review. */
export type DrillCardContentKind =
  | "grapheme-meaning"
  | "grapheme-pronunciation"
  | "word-characters-in-context"
  | "word-characters-in-sentence"
  | "word-pronunciation-in-sentence";

/**
 * The study subject a card is about, as an opaque string plus its kind.
 *
 * Mirrors the vocabulary `collection_morpheme.morpheme` already uses ("a grapheme or a
 * word") — the browse/collections/notes layer has modelled morphemes this way for a while;
 * the SRS layer simply never adopted it. Card rows carry this instead of a bare
 * `grapheme: string` so a non-grapheme subject has somewhere to live.
 */
export type CardSubject = {
  /** `quiz`: the text is the quiz's catalogue UUID key, not something the learner reads. */
  kind: "grapheme" | "quiz" | "word";
  text: string;
};

/**
 * Tone-number pinyin for a whole word — MULTI-syllable for a multi-character word, unlike the
 * single-syllable grapheme reading. Shared by every word card kind.
 */
type WordCardContentFields = {
  pinyin: string;
  word: string;
};

/**
 * What a `user_card` is about, discriminated by {@link CardContentKind}.
 *
 * The three word members carry identical fields but are spelled out separately rather than
 * collapsed into one member with a nested discriminator: every switch in this package branches on
 * `cardContent.kind`, and a nested field would fall through those silently.
 */
export type CardContent =
  | {
      grapheme: string;
      kind: "grapheme-meaning";
    }
  | {
      grapheme: string;
      kind: "grapheme-pronunciation";
      /** Tone-number pinyin, single syllable — the stable identity of the reading. */
      pinyin: string;
    }
  | ({ kind: "word-characters-in-context" } & WordCardContentFields)
  | ({ kind: "word-characters-in-sentence" } & WordCardContentFields)
  | ({ kind: "word-pronunciation-in-sentence" } & WordCardContentFields)
  | {
      /** The authored quiz's catalogue UUID key (`user_quiz_card.key`). */
      key: string;
      kind: "quiz";
    };

/** A card that is drilled: every {@link CardContent} but a quiz. */
export type DrillCardContent = Exclude<CardContent, { kind: "quiz" }>;

/** Narrow a {@link CardContent} to one kind. */
export type CardContentOfKind<TKind extends CardContentKind> = Extract<
  CardContent,
  { kind: TKind }
>;

/** The subject a card is about, independent of which kind of card it is. */
export function cardContentSubject(cardContent: CardContent): CardSubject {
  switch (cardContent.kind) {
    case "grapheme-meaning":
    case "grapheme-pronunciation": {
      return { kind: "grapheme", text: cardContent.grapheme };
    }
    case "word-characters-in-context":
    case "word-characters-in-sentence":
    case "word-pronunciation-in-sentence": {
      return { kind: "word", text: cardContent.word };
    }
    case "quiz": {
      return { kind: "quiz", text: cardContent.key };
    }
  }
}

/** The card kind a `DrillType` drills — the inverse of {@link cardContentDrillType}. */
export function drillTypeCardContentKind(drillType: DrillType): DrillCardContentKind {
  switch (drillType.kind) {
    case "meaning": {
      return "grapheme-meaning";
    }
    case "pronunciation": {
      return "grapheme-pronunciation";
    }
    case "word-sentence": {
      return "word-characters-in-context";
    }
    case "sentence-glyphs": {
      return "word-characters-in-sentence";
    }
    case "sentence-speech": {
      return "word-pronunciation-in-sentence";
    }
  }
}

/**
 * The `DrillType` kind a card kind is drilled as — {@link cardContentDrillType} for callers that
 * know only the kind (e.g. a due-card tile), not the payload a full `DrillType` carries.
 */
export function cardContentKindDrillKind(
  cardContentKind: DrillCardContentKind,
): DrillType["kind"] {
  switch (cardContentKind) {
    case "grapheme-meaning": {
      return "meaning";
    }
    case "grapheme-pronunciation": {
      return "pronunciation";
    }
    case "word-characters-in-context": {
      return "word-sentence";
    }
    case "word-characters-in-sentence": {
      return "sentence-glyphs";
    }
    case "word-pronunciation-in-sentence": {
      return "sentence-speech";
    }
  }
}

/** The `DrillType` a given card content is drilled as. */
export function cardContentDrillType(cardContent: DrillCardContent): DrillType {
  switch (cardContent.kind) {
    case "grapheme-meaning": {
      return { kind: "meaning" };
    }
    case "grapheme-pronunciation": {
      return { kind: "pronunciation", pinyin: cardContent.pinyin };
    }
    case "word-characters-in-context": {
      return { kind: "word-sentence", pinyin: cardContent.pinyin };
    }
    case "word-characters-in-sentence": {
      return { kind: "sentence-glyphs", pinyin: cardContent.pinyin };
    }
    case "word-pronunciation-in-sentence": {
      return { kind: "sentence-speech", pinyin: cardContent.pinyin };
    }
  }
}
